// 星盘锁（月亮塔罗的解密层）：每章一个星盘，谜诗 + 按顺序放入的符文 + 解开后《月亮之书》的一页。
// 数据在 content/themes/<theme>/puzzles.json（随 JS 打包，不走 public/），tools/validate.ts 校验。
import tarotRaw from '../../content/themes/tarot/puzzles.json';
import type { Manifest } from './manifest';

export type BookPage = { title: string; text: string };
export type Dial = {
  /** 章节下标（theme.chapters） */
  chapter: number;
  title: string;
  /** 谜诗，4–5 行；每行用画面指向一张牌，不点牌名 */
  poem: string[];
  /** 按顺序放进星盘的符文（用地点 id 表示，每张牌一个符文），4–5 个，都在本章 */
  answer: string[];
  page: BookPage;
};
export type ThemePuzzles = { version: 1; theme: string; book: string; dials: Dial[] };

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** 一页书的句数（按 。！？ 断句） */
export const sentenceCount = (t: string): number => t.split(/[。！？]/).filter((s) => s.trim().length > 0).length;

/** 结构 + 与 manifest 的交叉检查；返回全部问题（空 = 合法） */
export function puzzlesIssues(raw: unknown, m: Manifest): string[] {
  const issues: string[] = [];
  const bad = (path: string, msg: string) => issues.push(`puzzles.${path}: ${msg}`);
  if (!isObj(raw)) return ['puzzles: must be a JSON object'];
  for (const k of Object.keys(raw)) if (!['version', 'theme', 'book', 'dials'].includes(k)) bad(k, 'unknown field');
  if (raw.version !== 1) bad('version', 'must be 1');
  if (raw.theme !== m.theme.id) bad('theme', `must be '${m.theme.id}'`);
  if (!isStr(raw.book)) bad('book', 'must be a non-empty string');
  const chapters = m.theme.chapters ?? [];
  if (!Array.isArray(raw.dials)) return [...issues, 'puzzles.dials: must be an array'];
  if (raw.dials.length !== chapters.length) bad('dials', `must have one dial per chapter (${chapters.length}), has ${raw.dials.length}`);
  const byId = new Map(m.places.map((p) => [p.id, p]));
  raw.dials.forEach((d, i) => {
    const path = `dials[${i}]`;
    if (!isObj(d)) return bad(path, 'must be an object');
    for (const k of Object.keys(d)) if (!['chapter', 'title', 'poem', 'answer', 'page'].includes(k)) bad(`${path}.${k}`, 'unknown field');
    if (d.chapter !== i) bad(`${path}.chapter`, `must be ${i} (dials in chapter order)`);
    const ch = chapters[i];
    if (!isStr(d.title)) bad(`${path}.title`, 'must be a non-empty string');
    if (!Array.isArray(d.poem) || d.poem.length < 4 || d.poem.length > 5 || !d.poem.every(isStr)) bad(`${path}.poem`, 'must be 4–5 non-empty lines');
    const last = i === chapters.length - 1;
    const want = last ? 5 : 4;
    if (!Array.isArray(d.answer) || !d.answer.every(isStr)) bad(`${path}.answer`, 'must be an array of place ids');
    else {
      if (d.answer.length !== want) bad(`${path}.answer`, `must have ${want} slots, has ${d.answer.length}`);
      if (new Set(d.answer).size !== d.answer.length) bad(`${path}.answer`, 'runes must not repeat');
      for (const id of d.answer) {
        const p = byId.get(id);
        if (!p) bad(`${path}.answer`, `unknown place '${id}'`);
        else if (!p.rune) bad(`${path}.answer`, `place '${id}' has no rune`);
        else if (ch && (p.unlockOrder < ch.from || p.unlockOrder > ch.to)) bad(`${path}.answer`, `'${id}' is not in chapter ${i} (${ch.from}–${ch.to})`);
      }
      if (ch && d.answer.length >= ch.to - ch.from + 1) bad(`${path}.answer`, 'must leave some of the chapter runes out as distractors');
    }
    if (ch) for (let o = ch.from; o <= ch.to; o++) {
      const p = m.places.find((q) => q.unlockOrder === o);
      if (p && !p.rune) bad(path, `place '${p.id}' in this chapter has no rune`);
    }
    const pg = d.page;
    if (!isObj(pg) || !isStr(pg.title) || !isStr(pg.text)) bad(`${path}.page`, 'must be { title, text }');
    else {
      for (const k of Object.keys(pg)) if (!['title', 'text'].includes(k)) bad(`${path}.page.${k}`, 'unknown field');
      const n = sentenceCount(pg.text as string);
      if (n < 3 || n > 5) bad(`${path}.page.text`, `must be 3–5 sentences, has ${n}`);
    }
  });
  return issues;
}

/** 各主题的星盘（目前只有塔罗）；app 启动时再按 manifest 校验一遍 */
export const PUZZLES: Record<string, ThemePuzzles> = { tarot: tarotRaw as ThemePuzzles };

export const puzzlesOf = (themeId: string): ThemePuzzles | null => PUZZLES[themeId] ?? null;

/** 某章的星盘（没有 = null） */
export const dialOf = (themeId: string, chapter: number): Dial | null => puzzlesOf(themeId)?.dials.find((d) => d.chapter === chapter) ?? null;
